/**
 * Shared by the /looker filter-bar pills (data as of, backfill): dismissal and
 * a placement that keeps the detail panel inside the viewport. The pills wrap
 * anywhere in the control band, so neither `right-0` nor `left-0` is safe at
 * 500px: the panel is right-aligned with its pill, then clamped to a gutter.
 *
 * @module components/looker/statusPopover
 */
import { useCallback, useEffect, useLayoutEffect, useState, type RefObject } from 'react';

/** The panel's width (w-72) and the page's side gutter, in px. */
export const POPOVER_WIDTH_PX = 288;
export const POPOVER_GUTTER_PX = 16;

export interface PanelPlacement {
  /** px from the pill wrapper's left edge. */
  left: number;
  width: number;
}

/**
 * Right-aligned with the anchor where it fits, never past either gutter.
 * Pure, so the 500px case is testable without a layout engine.
 */
export const panelPlacement = (anchorLeft: number, anchorRight: number, viewportWidth: number): PanelPlacement => {
  const width = Math.max(0, Math.min(POPOVER_WIDTH_PX, viewportWidth - 2 * POPOVER_GUTTER_PX));
  const ideal = anchorRight - width;
  const viewportLeft = Math.min(Math.max(ideal, POPOVER_GUTTER_PX), viewportWidth - POPOVER_GUTTER_PX - width);
  return { left: viewportLeft - anchorLeft, width };
};

/**
 * Where the open panel goes, measured from `root` once, when it opens. A width
 * change closes the panel (useDismiss) rather than re-measuring: on `resize`
 * the filter bar has not reflowed yet, and a re-measure there landed the panel
 * at left = -97px (1399 → 500px, live).
 */
export const usePanelPlacement = (open: boolean, root: RefObject<HTMLElement>): PanelPlacement | null => {
  const [placement, setPlacement] = useState<PanelPlacement | null>(null);
  const measure = useCallback(() => {
    const el = root.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPlacement(panelPlacement(r.left, r.right, window.innerWidth));
  }, [root]);
  useLayoutEffect(() => {
    if (open) measure();
  }, [open, measure]);
  return open ? placement : null;
};

/**
 * Closes on Escape, on a press outside `root`, and when the viewport WIDTH
 * changes, while open. Height alone does not close it: a phone's URL bar
 * collapsing on scroll fires `resize` too.
 */
export const useDismiss = (open: boolean, root: RefObject<HTMLElement>, close: () => void): void => {
  useEffect(() => {
    if (!open) return;
    const widthAtOpen = window.innerWidth;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    const onDown = (e: MouseEvent) => {
      if (root.current && e.target instanceof Node && !root.current.contains(e.target)) close();
    };
    const onResize = () => {
      if (window.innerWidth !== widthAtOpen) close();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('resize', onResize);
    };
  }, [open, root, close]);
};

/** The panel's classes: no left/right utility, the placement sets `left` and `width`. */
export const POPOVER_PANEL_CLASS =
  'absolute top-full z-20 mt-2 w-72 max-w-[calc(100vw-2rem)] rounded-xl border border-neutral-200 bg-white p-3 text-sm shadow-lg';
